/**
 * Shared model for the org-pooled usage alert surfaces (sidebar banner,
 * workspace dashboard notice, header-bell notification).
 *
 * A single tier function keeps every surface in agreement about what counts
 * as "approaching" vs "frozen", and the dismissal helpers give the banners a
 * per-tier, per-org memory so a dismissed 75% warning never suppresses the
 * later 90% / frozen escalation.
 */

export type UsageTier = 0 | 75 | 90 | 100;

/** Map an org-pool percentage (+ frozen flag) to its alert tier. */
export function usageAlertTier(percentage: number, frozen = false): UsageTier {
  if (frozen || percentage >= 100) return 100;
  if (percentage >= 90) return 90;
  if (percentage >= 75) return 75;
  return 0;
}

/**
 * One source of truth for the alert palette so the sidebar banner and the page
 * notice can't drift. Approaching-limit is a warning (amber/orange), never blue
 * info; frozen is destructive. `icon` carries a leading-! because Alert's cva
 * sets `svg { color: currentColor }` via a descendant selector that outranks a
 * plain text-* utility on the svg.
 */
export interface UsageAlertTone {
  /** border + background, e.g. `border-amber-500/30 bg-amber-500/5`. */
  surface: string;
  text: string;
  icon: string;
}

export function usageAlertTone(percentage: number, frozen = false): UsageAlertTone {
  if (frozen || percentage >= 100)
    return { surface: 'border-destructive/30 bg-destructive/5', text: 'text-destructive', icon: '!text-destructive' };
  if (percentage >= 90)
    return { surface: 'border-orange-500/30 bg-orange-500/5', text: 'text-orange-500', icon: '!text-orange-500' };
  return { surface: 'border-amber-500/30 bg-amber-500/5', text: 'text-amber-500', icon: '!text-amber-500' };
}

const storageKey = (orgId: string) => `hookmyapp:usage-alert-dismissed:${orgId}`;

/**
 * The highest tier the user has dismissed for this org (0 = none).
 *
 * localStorage access throws in Safari private mode, when storage is disabled
 * by policy, or on quota errors. Fail closed to tier 0 ("nothing dismissed")
 * so an escalation still surfaces rather than crashing the alert.
 */
export function readDismissedTier(orgId: string): UsageTier {
  if (typeof window === 'undefined') return 0;
  try {
    const raw = Number(window.localStorage.getItem(storageKey(orgId)));
    return raw === 100 || raw === 90 || raw === 75 ? raw : 0;
  } catch {
    return 0;
  }
}

export function writeDismissedTier(orgId: string, tier: UsageTier): void {
  if (typeof window === 'undefined') return;
  try {
    if (tier === 0) window.localStorage.removeItem(storageKey(orgId));
    else window.localStorage.setItem(storageKey(orgId), String(tier));
  } catch {
    // Storage unavailable — dismissal just won't persist; not worth crashing.
  }
}
